import { fetchAuthSession } from "aws-amplify/auth";
import axios, { AxiosResponse } from "axios";
import { sessionStorageKeys } from "../hooks/useSelectedServiceCenter";
import { env } from "./env";

export function createHttpInstance(baseUrl: string = env.VITE_API_URL) {
  const baseConfig = { baseURL: baseUrl };
  const instance = axios.create(baseConfig);

  instance.interceptors.request.use(async (request) => {
    // add auth header with jwt if account is logged in and request is to the api url
    const session = await fetchAuthSession();

    request.headers.Authorization = `Bearer ${session.tokens?.accessToken.toString()}`;
    // Only set Content-Type to application/json if it's not FormData
    // FormData needs to set its own Content-Type with boundary
    if (!(request.data instanceof FormData)) {
      request.headers["Content-type"] = "application/json";
    }

    const serviceCenter = JSON.parse(
      sessionStorage.getItem(sessionStorageKeys.currentTabServiceCenter) ?? "{}"
    );
    request.headers["X-Service-Center-ID"] = serviceCenter.id;
    request.headers["TimeZone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    return request;
  });

  instance.interceptors.response.use((response: AxiosResponse) => {
    handleDates(response.data);
    return response;
  });

  return instance;
}

const isoDateFormat =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

const dateOnlyFormat =
  /^[1-9][0-9]{3}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

const isIsoDateString = (value: string): boolean => isoDateFormat.test(value);

const isDateOnlyString = (value: string): boolean => dateOnlyFormat.test(value);

/**
 * Optimistic-concurrency token field names whose UNTOUCHED ISO string must survive the
 * date-coercion below, so a mutation can later send it verbatim as an `If-Match` header.
 *
 * Context (OVR-001 / F-FE-17 item 6): `handleDates` coerces every ISO field — including the
 * `updatedAt` concurrency token — into a `Date` before app code sees it, which previously made it
 * impossible to send the raw token as `If-Match`. Rather than EXCLUDE `updatedAt` from coercion
 * (which would break every `z.date()` schema that relies on `updatedAt: Date` across all
 * workstreams), we ADDITIVELY preserve the raw string on a sibling key (`updatedAt` -> `updatedAtRaw`).
 * The `Date` coercion itself is unchanged (zero regression); Zod `.object()` parsing strips the
 * sibling for non-consumers, so blast radius is nil. Consumers (Customer-Profiles) capture
 * `updatedAtRaw` in their schemas and pass it to `buildIfMatchHeaders`. Extend this set only for a
 * genuinely new token field.
 */
const CONCURRENCY_TOKEN_KEYS = new Set<string>(["updatedAt"]);

/**
 * Field names that must stay a plain ISO string and are NEVER coerced into a `Date`,
 * regardless of which branch below would otherwise match them.
 *
 * `creditDecisionDate` (Location/Customer/Facility credit info) is system-computed,
 * read-only, and its consuming hooks/schemas declare it as `string | null` — but it's a
 * date-only value (`YYYY-MM-DD`), so without this exemption it silently matches
 * `isDateOnlyString` below and gets coerced to a `Date`, which then fails `z.string()`
 * validation in every credit-info Zod schema (`useLocationDetail.ts` et al.) on every
 * fetch where a decision has actually been recorded — a hard "Unexpected response format"
 * error, not a cosmetic one. Unlike `CONCURRENCY_TOKEN_KEYS`, this field is never sent back
 * to the server, so it only needs to be left alone, not also preserved on a sibling key.
 */
const RAW_STRING_DATE_KEYS = new Set<string>(["creditDecisionDate"]);

export const handleDates = (body: any) => {
  if (body === null || body === undefined || typeof body !== "object") {
    return body;
  }

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (typeof value === "string" && !RAW_STRING_DATE_KEYS.has(key)) {
      if (isIsoDateString(value)) {
        // Preserve the untouched ISO token BEFORE coercion so a mutation can send it as `If-Match`.
        // Only for concurrency-token keys, and only when absent — never clobber real backend data.
        // (Object.keys() above is a snapshot, so this newly-added sibling is not re-visited.)
        if (CONCURRENCY_TOKEN_KEYS.has(key)) {
          const rawKey = `${key}Raw`;
          if (!(rawKey in body)) {
            body[rawKey] = value;
          }
        }
        body[key] = new Date(value);
      }

      if (isDateOnlyString(value)) {
        const [year, month, day] = value.split("-").map(Number);

        body[key] = new Date(Date.UTC(year, month - 1, day));
      }
    } else if (typeof value === "object") handleDates(value);
  }
};

export const http = createHttpInstance(env.VITE_API_URL);
